import ClearIcon from "@mui/icons-material/CancelRounded";
import { ClickAwayListener } from "@mui/material";
import clsx from "clsx";
import { DynamicDates, toDayPickerDateRange } from "csi/utility/dateUtility";
import { getDialogZIndex } from "csi/utility/htmlUtility";
import { useLocalStore } from "designSystem/hooks";
import { Button, FlexColumn, FlexRowCenter, IconButton, NumberTextField, PopperInContainer, Tab, TabPanel, Tabs } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FC, useRef } from "react";
import { ClassNames, DayPicker } from "react-day-picker";
import ControlledTextField from "../textfield/ControlledTextField";
import styles from "./DateRangeTimePicker.module.css";
import { DateRangeTimePickerProps, DateRangeTimePickerStore, DateRangeTimePickerStoreContext, MAX_YEAR, MIN_YEAR, useDateRangeTimePickerStore } from "./DateRangeTimePickerStore";
import { TimeSlots } from "./TimeSlots";

const DateRangeTimePickerInternal: FC<DateRangeTimePickerProps> = observer(() => {
	const {
		dateFnsLocale,
		dynamicDates,
		dynamicDateString,
		handleDayClick,
		handleRangeSelectClick,
		handleTabChange,
		isRangeMode,
		range,
		selectedTabIndex,
		setDynamicDateString,
		showTabs,
		showTime,
	} = useDateRangeTimePickerStore();
	const classNames: ClassNames = {
		...styles,
	};
	const timeSlotToRef = useRef<HTMLDivElement>(null);
	const loc = useLocale();
	const rangeFromDate = range?.fromDate;

	return (
		<FlexColumn>
			<FlexRowCenter m={2}>
				{showTabs && (
					<Tabs onChange={handleTabChange} value={selectedTabIndex}>
						<Tab label={loc.resource("FixedDates")} />
						<Tab label={loc.resource("DynamicDates")} />
					</Tabs>
				)}
			</FlexRowCenter>
			<TabPanel index={0} selectedTabIndex={selectedTabIndex}>
				<FlexRowCenter>
					{isRangeMode ? (
						<DayPicker
							captionLayout="dropdown-buttons"
							classNames={classNames}
							fixedWeeks
							fromYear={MIN_YEAR}
							locale={dateFnsLocale}
							mode="range"
							month={rangeFromDate}
							numberOfMonths={1}
							onSelect={handleRangeSelectClick}
							selected={toDayPickerDateRange(range)}
							showOutsideDays
							toYear={MAX_YEAR}
						/>
					) : (
						<DayPicker
							captionLayout="dropdown-buttons"
							classNames={classNames}
							fixedWeeks
							fromYear={MIN_YEAR}
							locale={dateFnsLocale}
							mode="single"
							month={rangeFromDate}
							numberOfMonths={1}
							onSelect={handleDayClick}
							selected={rangeFromDate}
							showOutsideDays
							toYear={MAX_YEAR}
						/>
					)}
					{showTime && (
						<TimeSlots />
					)}
					{isRangeMode && showTime && (
						<TimeSlots
							isToTime
							ref={timeSlotToRef}
						/>
					)}
				</FlexRowCenter>
			</TabPanel>
			<TabPanel index={1} selectedTabIndex={selectedTabIndex}>
				<FlexColumn>
					{dynamicDates.map(({ id, res }) => {
						const selected = res === dynamicDateString;
						return (
							<Button
								className={clsx(
									styles.customComponentsDynamicDateButtons,
									selected && styles.customComponentsDynamicDateButtonsSelected,
								)}
								key={id + res}
								onClick={() => setDynamicDateString(res as DynamicDates, true)}
								res={res}
							/>
						);})}
				</FlexColumn>
			</TabPanel>
		</FlexColumn>
	);
});

const DateRangeTimePicker: FC<DateRangeTimePickerProps> = observer((props) => {
	const {
		initialRange,
		onRangeChange,
		onViolationChange,
		mode,
		selectedOperatorId,
		showTime,
	} = props;
	const loc = useLocale();
	const store = useLocalStore(async () => {
		const store = new DateRangeTimePickerStore(
			initialRange,
			loc,
			mode,
			onRangeChange,
			onViolationChange,
			selectedOperatorId,
			showTime,
		);
		return store;
	}, [mode, selectedOperatorId, showTime]);

	const {
		anchorEl,
		clickClearButton,
		closePopper,
		displayValue,
		dynamicDateString,
		invalidDateString,
		isNumberOfDaysSelected,
		numberOfDaysValue,
		onChangeInput,
		openPopper,
		setNumberOfDaysOnBlur,
		setNumberOfDaysOnChange,
	} = store || {};

	return (
		<DateRangeTimePickerStoreContext.Provider value={store}>
			{store && (
				<ClickAwayListener onClickAway={closePopper}>
					<FlexColumn>
						<FlexRowCenter>
							<ControlledTextField
								InputProps={{
									endAdornment: displayValue && (
										<IconButton
											onClick={clickClearButton}
											size="small"
										>
											<ClearIcon />
										</IconButton>
									),
									readOnly: !!dynamicDateString,
									style: { padding: 2 },
								}}
								error={!!invalidDateString}
								inputProps={{
									autoComplete: "off",
									className: styles.customComponentsControlledTextField,
								}}
								onChange={onChangeInput}
								onClick={openPopper}
								value={displayValue}
							/>
							{isNumberOfDaysSelected && (
								<NumberTextField
									inputProps={{
										className: styles.customComponentsNumberOfDaysTextField,
										onBlur: setNumberOfDaysOnBlur,
										onChange: setNumberOfDaysOnChange,
									}}
									value={numberOfDaysValue}
								/>
							)}
						</FlexRowCenter>
						<PopperInContainer
							anchorEl={anchorEl}
							flipOnOverflow
							sx={{ zIndex: getDialogZIndex() }}
						>
							<DateRangeTimePickerInternal {...props} />
						</PopperInContainer>
					</FlexColumn>
				</ClickAwayListener>
			)}
		</DateRangeTimePickerStoreContext.Provider>
	);
});

export default DateRangeTimePicker;
